import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Table, TableBody, TableCell, TableRow,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { 
  AccountCircle as ProfileIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { setCredentials } from '../store/slices/authSlice';

// Per-user IP exclusion list. Operators clicking their own delivery
// links inflate recipient analytics; tagging their IPs here removes
// them from view/started/confirmed counts on the Library.
function ExcludedIPsPanel() {
  const [data, setData] = useState({ ips: [], current_ip: null, current_ip_already_excluded: false });
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState('');
  const [manualIp, setManualIp] = useState('');

  const reload = async () => {
    try {
      const r = await api.get('/api/users/me/excluded-ips');
      setData(r.data || { ips: [] });
    } catch (_) { /* swallow — best-effort */ }
  };
  useEffect(() => { reload(); }, []);

  const addCurrent = async () => {
    if (!data.current_ip || data.current_ip_already_excluded) return;
    setBusy(true);
    try {
      await api.post('/api/users/me/excluded-ips', { ip_address: 'current', label: label || 'this device' });
      setLabel('');
      await reload();
    } finally { setBusy(false); }
  };
  const addManual = async () => {
    if (!manualIp.trim()) return;
    setBusy(true);
    try {
      await api.post('/api/users/me/excluded-ips', { ip_address: manualIp.trim(), label: label || null });
      setLabel(''); setManualIp('');
      await reload();
    } finally { setBusy(false); }
  };
  const remove = async (id) => {
    setBusy(true);
    try { await api.delete(`/api/users/me/excluded-ips/${id}`); await reload(); }
    finally { setBusy(false); }
  };

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" gutterBottom>My IPs (excluded from recipient analytics)</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add IP addresses you use yourself (office, home, mobile carrier) so your test clicks on
        delivery links don't pollute the recipient engagement counters in the Library. Reports
        and audit logs always see every event regardless — only the per-share view/started/confirmed
        counters apply this filter.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        {data.current_ip && (
          <Tooltip title={data.current_ip_already_excluded ? 'Already in your list' : 'Add the IP this request is coming from'}>
            <span>
              <Button
                size="small" variant="outlined" startIcon={<AddIcon />}
                onClick={addCurrent}
                disabled={busy || data.current_ip_already_excluded}
              >
                Add this device ({data.current_ip})
              </Button>
            </span>
          </Tooltip>
        )}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small" placeholder="Other IP (e.g. 73.x.x.x)"
            value={manualIp} onChange={(e) => setManualIp(e.target.value)}
            sx={{ width: 180 }}
          />
          <TextField
            size="small" placeholder="Label (optional)"
            value={label} onChange={(e) => setLabel(e.target.value)}
            sx={{ width: 160 }}
          />
          <Button size="small" onClick={addManual} disabled={busy || !manualIp.trim()}>Add</Button>
        </Box>
      </Box>
      {data.ips.length === 0 ? (
        <Typography variant="caption" color="text.secondary">No IPs added yet.</Typography>
      ) : (
        <Table size="small">
          <TableBody>
            {data.ips.map(r => (
              <TableRow key={r.id}>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', width: 160 }}>{r.ip_address}</TableCell>
                <TableCell><Typography variant="caption">{r.label || '—'}</Typography></TableCell>
                <TableCell sx={{ width: 60 }} align="right">
                  <Tooltip title="Remove">
                    <IconButton size="small" color="error" onClick={() => remove(r.id)} disabled={busy}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}


function Profile() {
  const { userInfo } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    full_name: userInfo?.full_name || '',
    email: userInfo?.email || '',
  });

  const handleEditClick = () => {
    setFormData({
      full_name: userInfo?.full_name || '',
      email: userInfo?.email || '',
    });
    setError('');
    setSuccess('');
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.patch('/api/users/profile/me', {
        full_name: formData.full_name || null,
        email: formData.email,
      });

      // Update the user info in Redux store
      dispatch(setCredentials({
        ...userInfo,
        full_name: response.data.full_name,
        email: response.data.email,
      }));

      setSuccess('Profile updated successfully');
      setTimeout(() => {
        setEditDialogOpen(false);
        setSuccess('');
      }, 1500);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Profile
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Avatar sx={{ width: 100, height: 100, mx: 'auto', mb: 2 }}>
              <ProfileIcon sx={{ fontSize: 60 }} />
            </Avatar>
            <Typography variant="h5" gutterBottom>
              {userInfo?.full_name || userInfo?.username}
            </Typography>
            <Chip
              label={userInfo?.role?.toLowerCase() === 'admin' ? 'Administrator' : 'User'}
              color={userInfo?.role?.toLowerCase() === 'admin' ? 'primary' : 'default'}
            />
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Account Information
              </Typography>
              <Button
                startIcon={<EditIcon />}
                onClick={handleEditClick}
                variant="outlined"
                size="small"
              >
                Edit Profile
              </Button>
            </Box>
            <List>
              <ListItem>
                <ListItemText
                  primary="Username"
                  secondary={userInfo?.username}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Email"
                  secondary={userInfo?.email}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Full Name"
                  secondary={userInfo?.full_name || 'Not set'}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Role"
                  secondary={userInfo?.role?.toLowerCase() === 'admin' ? 'Administrator' : 'Standard User'}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Account Status"
                  secondary={userInfo?.is_active ? 'Active' : 'Inactive'}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>

      <ExcludedIPsPanel />

      {/* Edit Profile Dialog */}
      <Dialog open={editDialogOpen} onClose={() => !saving && setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          <TextField
            id="profile-full-name"
            name="full_name"
            fullWidth
            label="Full Name"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            margin="normal"
            helperText="This name will appear as the sender in email notifications"
          />
          <TextField
            id="profile-email"
            name="email"
            fullWidth
            label="Email Address"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            margin="normal"
            required
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Your full name is used as the sender name in email notifications. 
              If you leave it empty, your username will be used instead.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !formData.email}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Profile;